/* eslint-disable no-underscore-dangle,no-await-in-loop,no-plusplus,@typescript-eslint/ban-ts-comment,@typescript-eslint/no-explicit-any */
import { execute } from 'crypto-pro-actual-cades-plugin';

import {
  AvailableCertificate,
  CADESPluginAsyncFixed,
  CertificateInfo,
  ExecutionContext,
  SignatureVerificationInfo,
  SignerInfo,
  VendorInfo,
} from 'SharedDir/declarations/crypto-pro';
import fileDownloader from 'SharedDir/utils/file-downloader';
import { blobToBase64 } from 'SharedDir/utils/file-readers';

// noinspection ES6RedundantAwait
/**
 * Класс для работы с плагином КриптоПро ЭЦП Browser plug-in.
 * Основан на использовании библиотеки https://github.com/vgoma/crypto-pro
 * (форк crypto-pro-actual-cades-plugin — актуальный cadesplugin_api.js с Manifest V3).
 *
 * Почему не использовалась сама библиотека?
 * В ней отсутствуют нужные нам методы - подписание самого файла, а не его хэша,
 * совместное подписание.
 *
 * Однако, она предоставляет очень удобную обвязку
 * (обработка загрузки плагина и всякое такое) для работы с плагином напрямую.
 *
 * Которую мне очень не хотелось писать самому.
 *
 * На данный момент реализовано подписание (и совместное подписание)
 * самого содержимого открепленной подписью в формате PKCS#7
 */
export class CryptoContainer {
  /**
   * Флаг показывающий, что плагин cadesplugin загружен,
   * и можно приступать ко взаимодействию с ним.
   *
   * @type {boolean}
   */
  static loaded: boolean = false;

  private static cadesplugin: CADESPluginAsyncFixed;

  private static executionContext: ExecutionContext;

  /**
   * Список доступных сертификатов ЭЦП.
   * В этот список сертификат попадает, если его срок действия актуален
   * и для него доступен контейнер с закрытым ключом
   *
   * @type {Array<AvailableCertificate>}
   * @private
   */
  private static availableCertificates: Array<AvailableCertificate>;

  /**
   * Функция проверки готовности плагина ко взаимодействию
   * При отсутствии готовности будет сгенерирована ошибка
   *
   * @returns {Promise<boolean>}
   */
  static async ready(): Promise<boolean> {
    if (this.loaded) {
      return true;
    }
    return this.Init();
  }

  /**
   * "Открываем" хранилище сертификатов.
   *
   * Необходимо для чтения списка доступных для подписания сертификатов ЭЦП
   *
   * @returns {Promise<CAPICOM.StoreAsync>}
   * @private
   */
  private static async OpenStore() {
    try {
      const cadesStore = await this.cadesplugin.CreateObjectAsync('CAdESCOM.Store');
      await cadesStore.Open(
        this.cadesplugin.CADESCOM_CONTAINER_STORE as any,
        this.cadesplugin.CAPICOM_MY_STORE,
        this.cadesplugin.CAPICOM_STORE_OPEN_MAXIMUM_ALLOWED
      );
      return cadesStore;
    } catch (error: any) {
      console.error(error);
      throw new Error(
        this.executionContext._extractMeaningfulErrorMessage(error) ||
          'Ошибка при попытке доступа к хранилищу'
      );
    }
  }

  /**
   * Обертка для работы с хранилищем сертификатов.
   * Позволяет удобно обращаться к хранилищу сертификатов
   * избавляет от обязанности следить и администрировать его состояние (открыто/закрыто)
   *
   * @param {(cadesStore: CAPICOM.StoreAsync) => Promise<T>} fn
   * @returns {Promise<T>}
   * @private
   */
  private static async WithStore<T>(
    fn: (cadesStore: any) => Promise<T>
  ): Promise<T> {
    const cadesStore = await this.OpenStore();
    try {
      return await fn(cadesStore);
    } catch (e: any) {
      throw new Error(
        this.executionContext._extractMeaningfulErrorMessage(e) || 'Что-то пошло не так!'
      );
    } finally {
      await cadesStore.Close();
    }
  }

  /**
   * Инициализация класса. Плагин пытается получить доступ к КриптоПро машины пользователя.
   * Обычно (всегда) пользователь должен разрешить скрипту с конкретного домена доступ к КриптоПро.
   *
   * Как только доступ получен, сохраняем контекст который нам заботливо выгружен *сrypto-pro*.
   *
   * @returns {Promise<boolean>}
   * @constructor
   */
  static async Init(): Promise<boolean> {
    try {
      this.executionContext = await execute((context) => context);
      ({ cadesplugin: this.cadesplugin } = this.executionContext);
      this.loaded = true;
      return this.loaded;
    } catch (e: any) {
      console.error(e);
      throw new Error(e);
    }
  }

  /**
   * Получить список доступных сертификатов ЭЦП
   *
   * @param {boolean} disableCache - получить список сертификатов непосредственно из хранилища даже если есть кэш
   * @returns {Promise<Array<AvailableCertificate>>}
   */
  static async getAvailableCertificates(
    disableCache: boolean = false
  ): Promise<Array<AvailableCertificate>> {
    if (typeof this.availableCertificates === 'undefined' || disableCache) {
      this.availableCertificates = await this.fetchAvailableCertificates();
    }
    return this.availableCertificates;
  }

  /**
   * Получаем сертификат по его отпечатку (Thumbprint или SHA1_HASH)
   *
   * @param {string} thumbprint
   * @returns {Promise<CAPICOM.CertificateAsync>}
   */
  static async getCertificate(thumbprint: string): Promise<any> {
    return this.WithStore(async (cadesStore) => {
      const certificates = await cadesStore.Certificates;
      const result = await certificates.Find(
        this.cadesplugin.CAPICOM_CERTIFICATE_FIND_SHA1_HASH,
        thumbprint
      );
      if ((await result.Count) > 0) {
        return result.Item(1);
      }
      throw new Error(`Certificate with thumbprint "${thumbprint}" not found!`);
    });
  }

  /**
   * Подписываем строку или файл открепленной подписью в формате PKCS#7
   * Доступно подписание с комментарием который будет подписан и сохранен в качестве
   * атрибута с именем _CADESCOM_AUTHENTICATED_ATTRIBUTE_DOCUMENT_DESCRIPTION_
   *
   * @param {string | Blob} message
   * @param {string} thumbprint
   * @param {string} comment - комментарий
   * @returns {Promise<string>}
   */
  static async SignData(
    message: string | Blob,
    thumbprint: string,
    comment: string = ''
  ): Promise<string> {
    try {
      const data = await this.prepareData(message);
      const signer = await this.cadesplugin.CreateObjectAsync('CAdESCOM.CPSigner');

      await signer.propset_Certificate(await this.getCertificate(thumbprint));
      await signer.propset_CheckCertificate(true);
      await this.addDescriptionAttribute(signer, comment);

      const signedData = await this.cadesplugin.CreateObjectAsync('CAdESCOM.CadesSignedData');
      await signedData.propset_ContentEncoding(this.cadesplugin.CADESCOM_BASE64_TO_BINARY);
      await signedData.propset_Content(data);

      return signedData.SignCades(signer, this.cadesplugin.CADESCOM_PKCS7_TYPE, true);
    } catch (err: any) {
      console.error(err);
      throw new Error(
        this.executionContext._extractMeaningfulErrorMessage(err) || 'Что-то пошло не так!!!'
      );
    }
  }

  /**
   * Метод добавляет атрибут с названием _CADESCOM_AUTHENTICATED_ATTRIBUTE_DOCUMENT_DESCRIPTION_
   * к объекту-подписчику _CAdESCOM.CPSignerAsync_
   *
   * @param {CAdESCOM.CPSignerAsync} signer
   * @param {string} comment
   * @returns {Promise<void>}
   * @private
   */
  private static async addDescriptionAttribute(signer: any, comment: string) {
    const attrs = await signer.AuthenticatedAttributes2;
    const commentAttr = await this.cadesplugin.CreateObjectAsync('CAdESCOM.CPAttribute');
    await commentAttr.propset_Name(
      this.cadesplugin.CADESCOM_AUTHENTICATED_ATTRIBUTE_DOCUMENT_DESCRIPTION
    );
    await commentAttr.propset_Value(comment);
    await attrs.Add(commentAttr);
  }

  /**
   * Если для подписания был передан файл, то необходимо его сначала закодировать в Base64
   * @param {string | Blob} message
   * @returns {Promise<string>}
   * @private
   */
  private static async prepareData(message: string | Blob) {
    let data: string;
    if (message instanceof Blob) {
      data = await blobToBase64(message);
      const header = ';base64,';
      return data.substring(data.indexOf(header) + header.length);
    }

    return message;
  }

  /**
   * Проверяем валидность открепленной цифровой подписи в формате PKCS#7
   *
   * @param {string | Blob} messageInBase64
   * @param {string} signature
   * @returns {Promise<SignatureVerificationInfo>}
   * @constructor
   */
  static async VerifySignature(
    messageInBase64: string | Blob,
    signature: string
  ): Promise<SignatureVerificationInfo> {
    try {
      const signedData = await this.cadesplugin.CreateObjectAsync('CAdESCOM.CadesSignedData');
      await signedData.propset_ContentEncoding(this.cadesplugin.CADESCOM_BASE64_TO_BINARY);

      const data = await this.prepareData(messageInBase64);

      await signedData.propset_Content(data);
      await signedData.VerifyCades(signature, this.cadesplugin.CADESCOM_PKCS7_TYPE, true);

      const certificates = await this.extractCertificates(signedData);
      const signers = await this.extractSigners(signedData);
      return { certificates, signers };
    } catch (err: any) {
      console.error(err);
      throw new Error(
        this.executionContext._extractMeaningfulErrorMessage(err) || 'Что-то пошло не так!!!'
      );
    }
  }

  /**
   * Извлекаем информацию о подписантах из верифицированной ЭЦП
   *
   * @param {CAdESCOM.CadesSignedDataAsync} signedData
   * @returns {Promise<Array<SignerInfo>>}
   * @private
   */
  private static async extractSigners(signedData: any) {
    const signers = [] as Array<SignerInfo>;
    // @ts-ignore
    const sign = (await signedData.Signers) as any;
    const signLength = await sign.Count;
    for (let i = 1; i <= signLength; i++) {
      const signer = await sign.Item(i);
      const attrs = await this.getSignAttributes(signer);
      const filteredAttrs = await this.getAttributeByType(
        attrs,
        this.cadesplugin.CADESCOM_AUTHENTICATED_ATTRIBUTE_DOCUMENT_DESCRIPTION
      );
      const [commentAttr] = filteredAttrs;
      let description = '';
      if (commentAttr) {
        description = await commentAttr.Value;
      }
      const certificate = await signer.Certificate;
      signers.push({
        signingTime: new Date((await signer.SigningTime) as any),
        comment: description,
        ...(await this.getCertificateInfo(certificate)),
      });
    }
    return signers;
  }

  /**
   * Извлекаем информацию о сертификатах в верифицированной ЭЦП
   *
   * @param {CAdESCOM.CadesSignedDataAsync} signedData
   * @returns {Promise<Array<CertificateInfo>>}
   * @private
   */
  private static async extractCertificates(signedData: any) {
    const certificates = [] as Array<CertificateInfo>;
    const certs = await signedData.Certificates;
    const certificatesLength = await certs.Count;
    for (let i = 1; i <= certificatesLength; i++) {
      certificates.push(await this.getCertificateInfo(await certs.Item(i)));
    }
    return certificates;
  }

  /**
   * Информацию о плагине и криптопровайдере
   *
   * @returns {Promise<VendorInfo>}
   * @constructor
   */
  static async Check(): Promise<VendorInfo> {
    const about = await this.cadesplugin.CreateObjectAsync('CAdESCOM.About');
    return {
      providerName: await about.CSPName(),
      providerVersion: await about.ProviderVersion(),
      pluginVersion: await about.Version,
    };
  }

  /**
   * Достаем из хранилища все сертификаты с актуальным сроком годности
   * и с наличием на устройстве доступа к их закрытому ключу
   *
   * @returns {Promise<Array<AvailableCertificate>>}
   * @private
   */
  private static async fetchAvailableCertificates() {
    return this.WithStore(async (cadesStore) => {
      const available: Array<AvailableCertificate> = [];
      const certificates = await cadesStore.Certificates;
      const length = await certificates.Count;
      for (let i = 1; i <= length; i++) {
        const cert = await certificates.Item(i);
        const isValid = await (await cert.IsValid()).Result;
        const hasPK = await cert.HasPrivateKey();
        if (isValid && hasPK) {
          available.push(await this.getCertificateInfo(cert));
        }
      }
      return available;
    });
  }

  /**
   * Извлекаем из сертификата информацию о владельце
   * издателе сроках, в общем все что необходимо для его верификации
   *
   * @param {CAPICOM.CertificateAsync | CAPICOM.Certificate} cert
   * @returns {Promise<CertificateInfo>}
   * @private
   */
  private static async getCertificateInfo(cert: any): Promise<CertificateInfo> {
    return {
      thumbprint: await cert.Thumbprint,
      serialNumber: await cert.SerialNumber,
      subjectName: await cert.GetInfo(
        this.cadesplugin.CAPICOM_CERT_INFO_SUBJECT_SIMPLE_NAME as any
      ),
      issuerName: await cert.GetInfo(this.cadesplugin.CAPICOM_CERT_INFO_ISSUER_SIMPLE_NAME as any),
      validFrom: new Date((await cert.ValidFromDate) as unknown as string),
      validTo: new Date((await cert.ValidToDate) as unknown as string),
      isValid: await (await cert.IsValid()).Result,
    };
  }

  /**
   * Добавляем параллельную открепленную цифровую подпись в формате PKCS#7
   * к уже существующей открепленной подписи в формате PKCS#7.
   *
   * Доступно подписание с комментарием который будет подписан и сохранен в качестве
   * атрибута с именем _CADESCOM_AUTHENTICATED_ATTRIBUTE_DOCUMENT_DESCRIPTION_
   *
   * @param {string | Blob} messageInBase64
   * @param {string} signature
   * @param {string} thumbprint
   * @param {string} comment
   * @returns {Promise<any>}
   * @constructor
   */
  static async CoSignData(
    messageInBase64: string | Blob,
    signature: string,
    thumbprint: string,
    comment: string = ''
  ): Promise<string> {
    try {
      const signedData = await this.cadesplugin.CreateObjectAsync('CAdESCOM.CadesSignedData');
      await signedData.propset_ContentEncoding(this.cadesplugin.CADESCOM_BASE64_TO_BINARY);
      const data = await this.prepareData(messageInBase64);
      await signedData.propset_Content(data);
      await signedData.VerifyCades(signature, this.cadesplugin.CADESCOM_PKCS7_TYPE, true);

      const signer = await this.cadesplugin.CreateObjectAsync('CAdESCOM.CPSigner');
      await signer.propset_Certificate(await this.getCertificate(thumbprint));
      await signer.propset_CheckCertificate(true);

      await this.addDescriptionAttribute(signer, comment);

      // @ts-ignore
      // return await signedData.CoSignCades(
      return signedData.CoSignCades(
        signer,
        this.cadesplugin.CADESCOM_PKCS7_TYPE,
        this.cadesplugin.CADESCOM_ENCODE_BASE64
      );
    } catch (err: any) {
      console.error(err);
      throw new Error(
        this.executionContext._extractMeaningfulErrorMessage(err) || 'Что-то пошло не так!!!'
      );
    }
  }

  /**
   * Извлекаем массив подписанных атрибутов из "подписчика"
   *
   * @param {CAdESCOM.CPSigner} signer
   * @returns {Promise<Array<CAdESCOM.CPAttribute>>}
   * @private
   */
  private static async getSignAttributes(signer: any) {
    const attrs = await signer.AuthenticatedAttributes2;
    const attrsCount = await attrs.Count;
    const arrayAttributes = [] as Array<any>;
    for (let j = 1; j <= attrsCount; j++) {
      const item = await attrs.Item(j);
      arrayAttributes.push(item);
    }

    return arrayAttributes;
  }

  /**
   * Фильтруем подписанные атрибуты по указанному типу.
   *
   * Хотя в концепции CAPICOM это название атрибута
   *
   * @param {Array<CAdESCOM.CPAttribute>} attrs
   * @param type
   * @returns {Promise<Array<CAdESCOM.CPAttribute>>}
   * @private
   */
  private static async getAttributeByType(attrs: Array<any>, type: any) {
    const filtered = [] as Array<any>;
    for (let i = 0; i < attrs.length - 1; i++) {
      try {
        const name = await attrs[i].Name;
        if (name === type) {
          filtered.push(attrs[i]);
        }
      } catch (e) {
        console.error(e);
      }
    }
    return filtered;
  }

  /**
   * Преобразование представления файла в формате base64 в файл
   * @param base64String
   * @param filename
   */
  private static async base64ToFile(base64String, filename) {
    // Шаг 1: Удалить префикс и декодировать Base64
    const byteString = atob(base64String);

    // Шаг 2: Преобразовать в массив байтов
    const byteArray = new Uint8Array(byteString.length);
    for (let i = 0; i < byteString.length; i++) {
      byteArray[i] = byteString.charCodeAt(i);
    }

    // Шаг 3: Создать Blob
    const blob = new Blob([byteArray], { type: 'application/pkix-cert' });

    // Шаг 4: Создать File (наследник Blob)
    return new File([blob], filename, { type: 'application/pkix-cert' });
  }

  /**
   * Получение файла по thumbprint
   * @param thumbprint
   * @param filename
   */
  static async getCertFile(thumbprint: string, filename: string = 'certificate.cer') {
    const cert = await this.getCertificate(thumbprint);
    const base64 = await cert.Export(this.cadesplugin.CADESCOM_ENCODE_BASE64);
    return this.base64ToFile(base64, filename);
  }
}

/**
 * Скачиваем открепленную ЭЦП в виде текстового файла
 * @param {string} signature
 * @param {string} fileName
 * @param {boolean} binary - флаг регулирующий формат подписи
 */
export const downloadSignature = (
  signature: string,
  fileName: string,
  binary: boolean = false
): void => {
  if (binary) {
    // eslint-disable-next-line no-param-reassign
    signature = atob(signature);
  }
  fileDownloader.saveFile(new Blob([signature], { type: 'text/plain' }), fileName);
};
